import { ref } from "vue";
import { MsalNavigationClient } from "@/services/msalNavigationClient";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    BrowserAuthError,
    type AccountInfo,
    type AuthenticationResult,
} from "@azure/msal-browser";
import { msalConfig, loginRequest, apiTokenRequest } from "@/config/auth";

const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.setNavigationClient(new MsalNavigationClient());

const user = ref<AccountInfo | null>(null);
const isAuthenticated = ref(false);
const isLoading = ref(false);
const isReady = ref(false);
const interactionInProgress = ref(false);
const authResponseState = ref<string | null>(null);

let initializePromise: Promise<void> | null = null;

function encodeAuthState(returnPath: string): string {
    return JSON.stringify({ returnPath });
}

function decodeAuthState(state?: string | null): string {
    if (!state) return "/";

    try {
        const parsed = JSON.parse(state);
        return typeof parsed.returnPath === "string" ? parsed.returnPath : "/";
    } catch {
        return "/";
    }
}

async function initializeMsal(): Promise<void> {
    if (initializePromise) {
        return initializePromise;
    }

    initializePromise = (async () => {
        await msalInstance.initialize();

        interactionInProgress.value = true;
        try {
            const redirectResponse: AuthenticationResult | null =
                await msalInstance.handleRedirectPromise();

            authResponseState.value = redirectResponse?.state ?? null;

            if (redirectResponse?.account) {
                msalInstance.setActiveAccount(redirectResponse.account);
            }
        } finally {
            interactionInProgress.value = false;
        }

        const account =
            msalInstance.getActiveAccount() ||
            msalInstance.getAllAccounts()[0] ||
            null;

        if (account) {
            msalInstance.setActiveAccount(account);
            user.value = account;
            isAuthenticated.value = true;
        } else {
            user.value = null;
            isAuthenticated.value = false;
        }

        isReady.value = true;
    })();

    return initializePromise;
}

async function refreshUser(): Promise<void> {
    await initializeMsal();

    const account =
        msalInstance.getActiveAccount() ||
        msalInstance.getAllAccounts()[0] ||
        null;

    if (account) {
        msalInstance.setActiveAccount(account);
        user.value = account;
        isAuthenticated.value = true;
    } else {
        user.value = null;
        isAuthenticated.value = false;
    }
}

async function login(returnPath = "/"): Promise<void> {
    await initializeMsal();

    if (interactionInProgress.value) {
        return;
    }

    isLoading.value = true;

    try {
        sessionStorage.setItem("post_login_redirect", returnPath);

        interactionInProgress.value = true;

        await msalInstance.loginRedirect({
            ...loginRequest,
            redirectUri: msalConfig.auth.redirectUri,
            state: encodeAuthState(returnPath),
        });
    } catch (error) {
        interactionInProgress.value = false;

        if (
            error instanceof BrowserAuthError &&
            error.errorCode === "interaction_in_progress"
        ) {
            return;
        }

        throw error;
    } finally {
        isLoading.value = false;
    }
}

async function logout(): Promise<void> {
    await initializeMsal();

    if (interactionInProgress.value) {
        return;
    }

    isLoading.value = true;

    try {
        authResponseState.value = null;
        sessionStorage.removeItem("post_login_redirect");
        interactionInProgress.value = true;
        await msalInstance.logoutRedirect();
    } finally {
        isLoading.value = false;
    }
}

async function beginReauth(returnPath?: string): Promise<void> {
    await initializeMsal();

    if (interactionInProgress.value) {
        return;
    }

    const target = returnPath || window.location.pathname + window.location.search;

    sessionStorage.setItem("reauth_notice", "1");
    sessionStorage.setItem("post_login_redirect", target);

    interactionInProgress.value = true;

    await msalInstance.acquireTokenRedirect({
        ...apiTokenRequest,
        redirectUri: msalConfig.auth.redirectUri,
        state: encodeAuthState(target),
        account:
            msalInstance.getActiveAccount() ||
            msalInstance.getAllAccounts()[0] ||
            undefined,
    });
}

async function getAccessToken(forceRefresh = false): Promise<string | null> {
    await initializeMsal();

    if (interactionInProgress.value) {
        return null;
    }

    let account = msalInstance.getActiveAccount();

    if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
            return null;
        }

        account = accounts[0];
        msalInstance.setActiveAccount(account);
    }

    try {
        const silentRedirectUri = `${window.location.origin}/auth/silent-callback.html`;

        const response = await msalInstance.acquireTokenSilent({
            ...apiTokenRequest,
            account,
            forceRefresh,
            redirectUri: silentRedirectUri,
        });

        return response.accessToken;
    } catch (error: any) {
        const message = String(error?.message || "");
        const errorCode = String(error?.errorCode || "");
        const subError = String(error?.subError || "");

        const shouldReauthenticate =
            error instanceof InteractionRequiredAuthError ||
            errorCode === "invalid_grant" ||
            errorCode === "timed_out" ||
            errorCode === "monitor_window_timeout" ||
            subError === "token_expired" ||
            message.includes("AADSTS70043") ||
            message.includes("AADSTS70044") ||
            message.includes("interaction_required");

        if (shouldReauthenticate) {
            await beginReauth();
            return null;
        }

        throw error;
    }
}

function getPostLoginRedirect(): string {
    const fromState = decodeAuthState(authResponseState.value);
    const fromSession = sessionStorage.getItem("post_login_redirect");

    // console.log("getPostLoginRedirect", {
    //     authResponseState: authResponseState.value,
    //     fromState,
    //     fromSession,
    // });

    if (fromState && fromState !== "/" && fromState !== "/auth/callback") {
        return fromState;
    }

    if (fromSession && fromSession !== "/auth/callback") {
        return fromSession;
    }

    return "/";
}

function clearPostLoginRedirect(): void {
    authResponseState.value = null;
    sessionStorage.removeItem("post_login_redirect");
}

export function useAuth() {
    return {
        user,
        isAuthenticated,
        isLoading,
        isReady,
        interactionInProgress,
        initializeMsal,
        refreshUser,
        login,
        logout,
        getAccessToken,
        getPostLoginRedirect,
        clearPostLoginRedirect,
        beginReauth,
    };
}